import React from 'react';

import { parse } from 'cookie';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { loadQuery, PreloadedQuery, RelayEnvironmentProvider } from 'react-relay';
import {
	GraphQLTaggedNode,
	OperationType,
	PreloadableConcreteRequest,
	VariablesOf,
} from 'relay-runtime';
import { v4 as uuidv4 } from 'uuid';

import { applyUFOFeatureGateConfig, initUFO } from '@/analytics/react-ufo/initUFO';
import { getAnalyticsWebClient } from '@/analytics/wac-analytics/get-analytics-web-client';
import { IntercomProperties, IntercomSettingsProperties } from '@/libraries/intercom/src/types';
import { RELAY_API_CONTENTFUL } from '@/utils/constant';
import { CONTENTFUL_GRAPHQL_API_URL } from '@/utils/urls.ts';

import {
	canonicalPathForHref,
	getCanonicalBaseUrl,
} from './bifrost-template/get-canonical-base-url';
import { AppPricingData } from './contentful/product-catalog';
import { initializeFeatureGatesClient } from './feature-gates/initialization';
import {
	AvailableProductsResponse,
	CcpTransactionAccountData,
	ServerSideRequestStatus,
	UserProfile,
	UserProfileResponse,
} from './libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { GrowthUnifiedProfile } from './libraries/growth-unified-profile-client/unified-profile.schema';
import { PageWrapper } from './page-wrapper-streaming';
import { initRelayEnvironment, RecordMap } from './relay-environment';
import { configureCastle } from './utils/castle';
import { DynamicConfigClient } from './utils/dynamic-configs/dynamic-config-client/dynamic-config-client';
import {
	overrideAjsAnonIdFromCookie,
	overrideCustomAttributes,
	isEdgeCachingPath,
} from './utils/fg-bootstrap-values-override-utils';
import { GridOverlayFunction } from './utils/grid-overlay';
import {
	extractOptanonConsentCookie,
	isPerformanceOnetrustEnabled,
} from './utils/onetrust-provider';
import { SentryIntlProvider } from './utils/sentry-intl';
import { LocationData, UserLocale } from './utils/useGeoCurrency';
import { processExperimentAndFeatureGateOverrides } from './utils/wac-browser-utility';
import { parseRoute } from './wac-routes';

window.BUILD_KEY = process.env['VERSION'];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
	identifiers: {
		analyticsAnonymousId: uuidv4(),
	},
};

configureCastle();

declare global {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore duplicate
	type FeatureFlagType = 'boolean' | 'string' | 'number';

	interface Window {
		IS_STATIC?: boolean;
		BUILD_KEY?: string;
		IS_STORYBOOK?: boolean;
		entryName?: string;
		SSR_languagePack: Record<string, string>;
		prices?: AppPricingData;
		availableProducts?: AvailableProductsResponse;
		userProfile?: UserProfile;
		userProfileStatus?: ServerSideRequestStatus;
		userContext?: UserProfileResponse;
		userContextStatus?: ServerSideRequestStatus;
		/**
		 * Whether the server saw a cloud session token cookie. Emitted into the
		 * bootstrap script so the client's first render can reach the same
		 * logged-in/out conclusion the server did, instead of diverging mid-hydration.
		 */
		hasCloudSessionTokenCookie?: boolean;
		keyword?: string;
		anchor?: string;
		subAnchor?: string;
		featureFlags: Record<string, FeatureFlagType>;
		cmsPageVariant?: string;
		mlTemplateIds?: string[];
		countryCode?: UserLocale;
		regionIsoCode?: string;
		optanonConsentCookie?: string;
		Intercom?: IntercomProperties;
		intercomSettings?: IntercomSettingsProperties;
		loggedOutContext?: GrowthUnifiedProfile;
		loggedOutContextStatus?: ServerSideRequestStatus;
		loggedInContext?: GrowthUnifiedProfile;
		loggedInContextStatus?: ServerSideRequestStatus;
		toggleGrid?: GridOverlayFunction;
		relayStoreSnapshot?: RecordMap;
		relayQueryRef?: PreloadedQuery<OperationType>;
		CONTENTFUL_BASE_URL_GQL: string;
		CONTENTFUL_SPACE_ID_PRODUCT_TEMPLATES: string;
		CONTENTFUL_ENVIRONMENT_PRODUCT_TEMPLATES: string;
		CONTENTFUL_DELIVERY_KEY_PRODUCT_TEMPLATES: string;
		transactionAccounts?: Array<CcpTransactionAccountData>;
		PRODUCT_CATALOG_DELIVERY_KEY: string;
		PRODUCT_CATALOG_SPACE: string;
		PRODUCT_CATALOG_LOCATION_BASE_URL: string;
	}
}

const SUPPORTED_LOCALES = [
	{ hrefLang: 'ja', langRoute: 'ja' },
	{ hrefLang: 'fr', langRoute: 'fr' },
	{ hrefLang: 'de', langRoute: 'de' },
	{ hrefLang: 'es', langRoute: 'es' },
	{ hrefLang: 'pt-BR', langRoute: 'br' },
	{ hrefLang: 'zh-Hans', langRoute: 'zh' },
	{ hrefLang: 'ko', langRoute: 'ko' },
	{ hrefLang: 'ru', langRoute: 'ru' },
	{ hrefLang: 'pl', langRoute: 'pl' },
	{ hrefLang: 'it', langRoute: 'it' },
	{ hrefLang: 'nl', langRoute: 'nl' },
];

export const init = <TQuery extends OperationType>(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Component: React.ComponentType<any>,
	query: GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>,
	queryVars: VariablesOf<TQuery>,
	relayApi?: typeof RELAY_API_CONTENTFUL | undefined,
) => {
	const analyticsWebClient = getAnalyticsWebClient();
	const { locale, path } = parseRoute(window.location);
	// Get location data for experiments and feature gates from server-provided data
	let locationData: LocationData | undefined;

	// Use server-provided data (no client-side API calls)
	if (typeof window !== 'undefined') {
		locationData = {
			countryIsoCode: window.countryCode || '',
			regionIsoCode: window.regionIsoCode || '',
		};
	}

	const isPerfOnetrustEnabled = isPerformanceOnetrustEnabled(
		typeof window !== 'undefined'
			? window.optanonConsentCookie
			: extractOptanonConsentCookie(document.cookie),
		locationData,
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	isPerfOnetrustEnabled && initUFO(analyticsWebClient, path);
	const parsedClientCookies = document.cookie ? parse(document.cookie) : {};
	const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : '';

	const { finalExperimentOverrides, finalFeatureGateOverrides } =
		processExperimentAndFeatureGateOverrides(parsedClientCookies, userAgent, isPerfOnetrustEnabled);

	if (isEdgeCachingPath(window.location)) {
		// Override for Edge Caching
		overrideAjsAnonIdFromCookie();
		overrideCustomAttributes();
	}

	initializeFeatureGatesClient(window.featureGateBootstrapValues, {
		overrideValues: {
			experimentOverrides: finalExperimentOverrides,
			featureGateOverrides: finalFeatureGateOverrides,
		},
		// if performance cookies are disabled, we will skip exposures
		localMode: !isPerfOnetrustEnabled,
	}).then(async () => {
		try {
			await DynamicConfigClient.getInstance().initialize({
				bootstrapValues: window.featureGateBootstrapValues,
			});
		} catch (error) {
			console.error('Failed to initialize DynamicConfigClient', error);
		}

		// GWP-13710: gates are ready now; opt react-ufo into CLS/TBT if enabled.
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		isPerfOnetrustEnabled && applyUFOFeatureGateConfig();

		const isStatic = typeof window !== 'undefined' && window.IS_STATIC;

		const snapshot = window.relayStoreSnapshot;

		let relayBaseUrl = undefined;
		let relayAuthKey = undefined;
		if (relayApi === RELAY_API_CONTENTFUL) {
			relayBaseUrl = CONTENTFUL_GRAPHQL_API_URL;
			relayAuthKey = window.CONTENTFUL_DELIVERY_KEY_PRODUCT_TEMPLATES;
		}

		const relayEnvironment = initRelayEnvironment(snapshot, relayBaseUrl, relayAuthKey);
		const queryRef = loadQuery(relayEnvironment, query, queryVars);

		const canonicalBaseUrl = getCanonicalBaseUrl(window.location.hostname, path);
		const pathForCanonical = path ? canonicalPathForHref(canonicalBaseUrl, path) : undefined;
		const route =
			pathForCanonical === undefined ? '' : pathForCanonical === '' ? '' : `/${pathForCanonical}`;

		const page = (
			<SentryIntlProvider
				locale={locale}
				messages={window.SSR_languagePack}
				cookies={document.cookie}
			>
				<RelayEnvironmentProvider environment={relayEnvironment}>
					{isStatic && <link rel="canonical" href={`${canonicalBaseUrl}${route}`} />}
					{isStatic && (
						<link rel="alternate" href={`${canonicalBaseUrl}${route}`} hrefLang="x-default" />
					)}
					{isStatic &&
						SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
							<link
								key={hrefLang}
								rel="alternate"
								href={`${canonicalBaseUrl}/${langRoute}${route}`}
								hrefLang={hrefLang}
							/>
						))}
					<PageWrapper
						analyticsWebClient={analyticsWebClient}
						location={window.location}
						pricing={window.prices}
						availableProducts={window.availableProducts}
						userProfile={window.userProfile}
						userProfileStatus={window.userProfileStatus}
						userContext={window.userContext}
						userContextStatus={window.userContextStatus}
						hasCloudSessionTokenCookie={window.hasCloudSessionTokenCookie}
						loggedOutContext={window.loggedOutContext}
						loggedOutContextStatus={window.loggedOutContextStatus}
						loggedInContext={window.loggedInContext}
						loggedInContextStatus={window.loggedInContextStatus}
						countryCode={window.countryCode}
						mlTemplateIds={window.mlTemplateIds}
						keyword={window.keyword}
						anchor={window.anchor}
						subAnchor={window.subAnchor}
						queryRef={queryRef}
						PageComponent={Component}
						transactionAccounts={window.transactionAccounts}
						userAgent={userAgent}
					/>
				</RelayEnvironmentProvider>
			</SentryIntlProvider>
		);

		if (isStatic) {
			// @ts-expect-error type
			const root = createRoot(document.getElementById('wac'));
			root.render(page);
		} else {
			hydrateRoot(
				// @ts-expect-error type
				document.getElementById('wac-root'),
				page,
				{
					onRecoverableError: (error, errorInfo) => {
						if (process.env['CI'] === 'true') {
							document.getElementById('wac-root')?.setAttribute('hydration-error', 'true');
						}
						if (process.env['MODE'] === 'dev') {
							console.log(`Recoverable Error (including Hydration): ${error}`, errorInfo);
						}
					},
				},
			);
		}
	});
};
